import { useQuery } from '@tanstack/react-query';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import type { ProjectIndexRowWire } from '@releve/shared';

/**
 * One row of the slim project index (the `project_index` DB view). An ALIAS
 * of the shared wire type — re-exported here so every projects grid site (the
 * list, the map adapter, the timeline, the edit dialog's feed) reaches the row
 * contract through this one hook.
 */
export type ProjectIndexRow = ProjectIndexRowWire;

/**
 * The COMPLETE slim project index for an org — the API pages the
 * `project_index` view past PostgREST's 1,000-row cap on a (created_at, id)
 * keyset cursor, so it is never silently truncated. Powers the Projects page's
 * list, map and timeline: the `ProjectFilterDocument` does ALL filtering in
 * the browser, which is why the endpoint takes ZERO query parameters and this
 * hook takes no options argument.
 *
 * The view projects `team_count` (COUNT of `project_team`), `places` (every
 * ACTIVE `project_places` link with its gis coordinates, primary first) and
 * `events_start_date` / `events_end_date` (MIN/MAX of `project_events`), so a
 * write to any of those tables changes an index row.
 *
 * ── Liveness contract ───────────────────────────────────────────────────────
 * The key is `['projects', orgId, 'index', {}]`.
 *
 * THE ENTITY PREFIX IS `'projects'` AND MUST STAY THAT WAY. It is the prefix
 * every writer below invalidates, which is the only reason this grid updates
 * after a write:
 *   - project rows: `useCreateProject`, `useUpdateProject` (both
 *     `['projects', orgId]`) and the image upload/remove in `useImageUpload`
 *     (bare `['projects']`);
 *   - `team_count`: every write in `useProjectTeamMutations` (add / edit /
 *     remove, `useProjectTeam.ts`) invalidates `['projects', orgId]`;
 *   - `places`: `useProjectPlaces` (link/unlink/primary), `CreateProjectDialog`'s
 *     post-create venue link, and `useUpdatePlace` / `useDeletePlace` in
 *     `usePlaces.ts` (a renamed place changes `place_name` + `search_text`; a
 *     deleted one drops its links) invalidate `['projects', orgId]`;
 *   - `project_type_name`: the three `useDualScopeTypes` mutations invalidate
 *     the bare `['projects']` prefix when `tableName === 'project_types'`;
 *   - `events_start_date` / `events_end_date` (MIN/MAX over `project_events`,
 *     the inherited Event start / end — 20260925220000): every event write in
 *     `useProjectEventMutations` (`useProjectEvents.ts`) invalidates
 *     `['projects', orgId]` — create and add-venue via `invalidatePlaceLinks`,
 *     update and delete directly. An event write therefore changes index rows;
 *   - `Dashboard` invalidates the bare `['projects']` prefix on refresh.
 *
 * Renaming the prefix (to `'project-index'`, say) would silently kill every
 * one of those — no type error, no other test failure, just a grid that stops
 * updating after a write. The only guard is the literal-key assertion in
 * `useProjectIndex.test.ts`; keep it.
 *
 * ── Known caveat: the carbon impact column ───────────────────────────────────
 * Carbon impact is NOT part of this index. The grid folds it in on the web side
 * from `useEntityCarbonEmissions` (`['carbonEmissions', orgId, 'by-entity']`,
 * 5-minute `staleTime`). Several ledger/import writers do not invalidate that
 * carbon key, so the impact column is fresh within ~5 minutes — the same
 * contract the legacy page had. Merging carbon into this endpoint would move it
 * under `['projects', …]`, which carbon writers do not invalidate either.
 *
 * `staleTime` (60s, from indexQueryOptions) only suppresses redundant
 * mount/focus refetches between mutations.
 */
export const useProjectIndex = (organizationId?: string) =>
  // No params: the index endpoint takes none, and the builder defaults key
  // slot [3] to {}. Passing an empty-ish object "for future filters" would
  // fragment the cache — don't.
  //
  // Key shape, enabled gate, {items} unwrap, staleTime, and the same-org
  // placeholder guard (no cross-org flash) all live in indexQueryOptions.
  useQuery(
    indexQueryOptions<ProjectIndexRowWire>({
      entity: 'projects',
      organizationId,
      path: `/api/v1/projects/orgs/${organizationId}/projects/index`,
    }),
  );
